import React, { useState, useEffect } from 'react';
import { AlertCircle, ChevronRight, X } from 'lucide-react';
import InputMask from 'react-input-mask';
import { supabase } from '../../lib/supabaseClient';
import { useLanguage } from '../../contexts/LanguageContext';

interface RequestFormProps {
  isPopup?: boolean;
  onClose?: () => void;
  source?: string;
}

function RequestForm({ isPopup = false, onClose, source = '' }: RequestFormProps) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    productLink: '',
    comment: '',
    name: '',
    phone: '',
  });

  // Load user data from localStorage on mount
  useEffect(() => {
    const firstName = localStorage.getItem('userFirstName') || '';
    const lastName = localStorage.getItem('userLastName') || '';
    const phone = localStorage.getItem('userPhone') || '';
    
    if (firstName && lastName) {
      setFormData(prev => ({
        ...prev,
        name: `${firstName} ${lastName}`,
        phone
      }));
    }
  }, []);
  const [formErrors, setFormErrors] = useState({
    name: '',
    phone: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validateForm = () => {
    const errors = {
      name: '',
      phone: '',
    };
    let isValid = true;

    if (!formData.name.trim()) {
      errors.name = 'Пожалуйста, введите имя';
      isValid = false;
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Пожалуйста, введите телефон';
      isValid = false;
    } else if (!/^\+?[0-9\s-()]+$/.test(formData.phone)) {
      errors.phone = 'Пожалуйста, введите корректный номер телефона';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitError(null);
    
    try {
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/6069-cargo-telegram`;

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          productLink: formData.productLink || '',
          comment: formData.comment || '',
          source: source || ''
        }),
      });

      // Log response details for debugging
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error('Ошибка при отправке заявки. Пожалуйста, попробуйте позже.');
      }

      let data;
      try {
        data = await response.json();
        
        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Ошибка при отправке заявки');
        }

        setSubmitSuccess(true);
        setFormData({
          productLink: '',
          comment: '',
          name: '',
          phone: '',
        });
      } catch (e) {
        console.error('Error parsing JSON:', e);
        throw new Error('Ошибка при обработке ответа сервера');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(error instanceof Error ? error.message : 'Произошла ошибка при отправке заявки');
      setSubmitSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle escape key for popup mode
  useEffect(() => {
    if (isPopup) {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose?.();
        }
      };
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [isPopup, onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  };

  const formContent = (
    <form onSubmit={handleSubmit} className="max-w-[780px] mx-auto space-y-8">
      <div>
        <label className={`block text-sm font-medium mb-2 ${isPopup ? 'text-gray-700' : 'text-white/80'}`}>
          {t('form.product')}
        </label>
        <textarea
          value={formData.productLink}
          onChange={(e) => setFormData(prev => ({ ...prev, productLink: e.target.value }))}
          className={`w-full px-4 py-3 rounded-lg transition-all ${
            isPopup 
              ? 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-[#1B3BA0] focus:border-transparent' 
              : 'bg-white/10 border border-white/20 text-white placeholder-white/50 focus:ring-2 focus:ring-white/30 focus:border-transparent backdrop-blur-sm'
          }`}
          rows={3}
          placeholder={t('form.product.placeholder')}
        />
      </div>

      <div>
        <label className={`block text-sm font-medium mb-2 ${isPopup ? 'text-gray-700' : 'text-white/80'}`}>
          {t('form.comment')}
        </label>
        <textarea
          value={formData.comment}
          onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
          className={`w-full px-4 py-3 rounded-lg transition-all ${
            isPopup 
              ? 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-[#1B3BA0] focus:border-transparent' 
              : 'bg-white/10 border border-white/20 text-white placeholder-white/50 focus:ring-2 focus:ring-white/30 focus:border-transparent backdrop-blur-sm'
          }`}
          rows={2}
          placeholder={t('form.comment.placeholder')}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className={`block text-sm font-medium mb-2 ${isPopup ? 'text-gray-700' : 'text-white/80'}`}>
            {t('form.name')}
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            className={`w-full px-4 py-3 rounded-lg transition-all ${
              isPopup 
                ? 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-[#1B3BA0] focus:border-transparent' 
                : 'bg-white/10 border border-white/20 text-white placeholder-white/50 focus:ring-2 focus:ring-white/30 focus:border-transparent backdrop-blur-sm'
            }`}
            placeholder={t('form.name.placeholder')}
            required
          />
          {formErrors.name && (
            <p className="mt-2 text-sm text-red-500 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {t('form.error.name')}
            </p>
          )}
        </div>

        <div>
          <label className={`block text-sm font-medium mb-2 ${isPopup ? 'text-gray-700' : 'text-white/80'}`}>
            {t('form.phone')}
          </label>
          <InputMask
            mask="+7 (999) 999-99-99"
            maskChar="_"
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            className={`w-full px-4 py-3 rounded-lg transition-all ${
              isPopup 
                ? 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-[#1B3BA0] focus:border-transparent' 
                : 'bg-white/10 border border-white/20 text-white placeholder-white/50 focus:ring-2 focus:ring-white/30 focus:border-transparent backdrop-blur-sm'
            }`}
            placeholder={t('form.phone.placeholder')}
            required
          />
          {formErrors.phone && (
            <p className="mt-2 text-sm text-red-500 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {formErrors.phone === 'Пожалуйста, введите телефон' ? t('form.error.phone') : t('form.error.phone.invalid')}
            </p>
          )}
        </div>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className={`w-full py-3 px-6 rounded-full transition-all font-medium flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl ${
          isPopup
            ? 'bg-[#1B3BA0] text-white hover:bg-[#0F2875]'
            : 'bg-white text-[#1B3BA0] hover:bg-[#99BBFF]'
        }`}
      >
        {isSubmitting ? t('form.submitting') : t('form.submit')}
        <ChevronRight className="w-5 h-5 ml-2" />
      </button>

      {submitError && (
        <div className="bg-red-50 text-red-800 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{submitError}</span>
        </div>
      )}

      {submitSuccess && (
        <div className="bg-green-50 text-green-800 rounded-lg p-4">
          {t('form.success')}
        </div>
      )}
    </form>
  );

  if (isPopup) {
    return (
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-6"
        onClick={handleOverlayClick}
      >
        <div 
          className="bg-white rounded-2xl p-8 w-full max-w-[780px] max-h-[90vh] overflow-y-auto shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold text-[#1d1d1f]">
              {t('contact.title')}
            </h2>
            <button
              onClick={() => onClose?.()}
              className="w-10 h-10 flex items-center justify-center text-gray-500 hover:text-gray-900 transition-all rounded-full hover:bg-gray-100 active:scale-95"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {formContent}
        </div>
      </div>
    );
  }

  return formContent;
}

export default RequestForm;